import { BlockT, BoxT, ClampPoint, ConstraintItem, Curve3, Cylinder, ENTITY_TYPE, FatLine, FrustumT, ICapturePoint, Line, LineMaterial, LineSegments2, LineT, ModelBase, ModelUtils, PlineAttributes, PlineT, Point, RenderMode, TnEngineContext, TnEngineExtraContext, ViewEditor } from "pytha";
import { Color, Vector3, Object3D, Object3DEventMap, Material, Box3, Group, LineDashedMaterial, LineSegments, Matrix4, MeshPhongMaterial, DoubleSide, HSL } from "three";
import { CableMiddleClamp } from "../clamp-points/CableMiddleClamp";


export class CableT extends PlineT {

    baseType = 'PlineT';
    type = 'CableT';
    group: { [key: number]: string[] } = {};
    extraData: any = {};
    renderType = 'AlwaysMesh';
    _verticesBuffer: number[] = [];
    _verticeColorsBuffer: number[] = [];

    meshRenderItemSize: number = 0;

    private _edgeVerticesBuffer: number[] = [];
    private _edgeVerticeColorsBuffer: number[] = [];
    readonly positionUpdateCallBack: boolean = true;

    constructor(_geoCurves: Curve3[] = [], plineAttributes: PlineAttributes[] = [], normal = new Vector3(0, 0, 1), uuid = null) {
        super(_geoCurves, plineAttributes, normal, uuid);
    }

    updateMaterialRenderKey() {
        let arr = [];
        arr.push(this.renderType);
        arr.push(this.context.renderContext.renderType);
        arr.push(this.isSelected ? "IsSelected" : "UnSelected");
        if (this.context.renderContext.renderType === RenderMode.WIREFRAME) {
            arr.push(this.lineDashSize);
            arr.push(this.lineGapSize);
            arr.push(this.lineDashScale);
        } else {
            arr.push(this.opacity)
        }
        arr.push(this.layerUuid);
        if (this.groupIds && this.groupIds.length > 0) {
            arr.push(...this.groupIds)
        }
        let key = arr.join('-');
        return key;
    }


    getVerticesBuffer(type?: string) {
        // 渲染类型改为AlwaysMesh 只返回面渲染
        return this._getCableMeshVerticesBuffer();
    }

    /**
     * 预计制作方法：把所有的线，构造为Cylinder（圆柱）
     * center为起点，半径为线宽，高度为线长，normal为线的方向向量
     * 然后调用圆柱的方法获取
     * 所得结果拼起来
     * @returns 
     */
    _getCableMeshVerticesBuffer() {
        let w2uMatrix: Matrix4 = new Matrix4();
        let u2wMatrix: Matrix4 = new Matrix4();
        let totalVerticesBuffer = [];
        let totalMeshRenderItemSize = 0;
        for (let i = 0; i < this._geoCurves.length; i++) {
            let line: Line = this._geoCurves[i] as Line;
            let verticesBuffer = [];
            let center = line.start.clone();
            let radius = this.plineAttributes[i].startWidth || 0.25;
            let height = line.distance();
            let normal = line.delta().normalize();
            let geo: Cylinder = new Cylinder(center, radius, height, normal);
            let axisX = this._calculateAxisX(normal);
            geo.axisX.set(axisX.x, axisX.y, axisX.z);
            let meshRenderItemSize = geo.getMeshVertexBuffer(verticesBuffer, 0, 1, 1, 1, w2uMatrix, u2wMatrix);
            totalVerticesBuffer = [...totalVerticesBuffer, ...verticesBuffer];
            totalMeshRenderItemSize += meshRenderItemSize;
        }
        this._verticesBuffer = totalVerticesBuffer;
        this.renderItemSize = totalMeshRenderItemSize;
        return this._verticesBuffer;
    }

    getColorsBuffer(type?: string) {
        // 渲染类型改为AlwaysMesh 只返回面渲染
        return this._getMeshColorsBuffer();
    }

    _getMeshColorsBuffer() {
        this._verticeColorsBuffer = [];
        let renderColor = this._getRenderColor();
        for (let i = 0; i < this.renderItemSize; i += 3) {
            this._verticeColorsBuffer[i] = renderColor.r;
            this._verticeColorsBuffer[i + 1] = renderColor.g;
            this._verticeColorsBuffer[i + 2] = renderColor.b;
        }
        return this._verticeColorsBuffer;
    }

    // 计算和给定向量垂直的向量
    _calculateAxisX = (normal: Vector3): Vector3 => {
        // 假定一个z轴向量
        let v3 = new Vector3(0, 0, 1);
        // 如果normal和z轴平行，则使用y轴
        if (normal.equals(v3)) {
            v3 = new Vector3(0, 1, 0);
        }
        // 计算叉积得到 axisX
        let axisX = new Vector3().crossVectors(normal, v3).normalize();
        return axisX;
    };


    attachClampPoints(editor: ViewEditor): ClampPoint[] {
        if (this.lock) {
            return [];
        }
        if (this.constraintList?.length > 0) {
            return []
        }
        // 创建夹点
        this.clampPoints = [];
        let pointList = this.pointList;
        if (pointList.length === 0) return this.clampPoints;
        for (let i = 0; i < this._geoCurves.length; i++) {
            let pointStart = this._geoCurves[i].getPointAt(0);
            let pointMid = this._geoCurves[i].getPointAt(0.5);
            if (i === 0) {
                let clampStart = new CableMiddleClamp(editor, this, pointStart, `${i}`, 'start');
                this.clampPoints.push(clampStart);
            } else {
                let clampStart = new CableMiddleClamp(editor, this, pointStart, `${i}`, null);
                this.clampPoints.push(clampStart);
            }

            if (i === this._geoCurves.length - 1) {
                let clampMid = new CableMiddleClamp(editor, this, pointMid, `center-${i}-${i + 1}`, null);
                this.clampPoints.push(clampMid);
                let pointEnd = this._geoCurves[i].getPointAt(1);
                let clampEnd = new CableMiddleClamp(editor, this, pointEnd, `${i + 1}`, 'end');
                this.clampPoints.push(clampEnd);
            } else {
                let clampMid = new CableMiddleClamp(editor, this, pointMid, `center-${i}-${i + 1}`, null);
                this.clampPoints.push(clampMid);
            }
        }
        return this.clampPoints;
    }

    // attachClampPoints(editor: ViewEditor): ClampPoint[] {
    //     if (this.lock) {
    //         return [];
    //     }
    //     if (this.constraintList?.length > 0) {
    //         return []
    //     }
    //     // 创建夹点
    //     this.clampPoints = [];
    //     let pointList = this.pointList;
    //     if (pointList.length === 0) return this.clampPoints;
    //     for (let i = 0; i < this._geoCurves.length; i++) {
    //         let pointStart = this._geoCurves[i].getPointAt(0);
    //         let pointMid = this._geoCurves[i].getPointAt(0.5);
    //         let clampStart = new CableMiddleClamp(editor, this, pointStart, `${i}`);
    //         this.clampPoints.push(clampStart);
    //         if (i === this._geoCurves.length - 1) {
    //             if (this.loop) {
    //                 let clampMid = new CableMiddleClamp(editor, this, pointMid, `center-${i}-${0}`);
    //                 this.clampPoints.push(clampMid);
    //             } else {
    //                 let clampMid = new CableMiddleClamp(editor, this, pointMid, `center-${i}-${i + 1}`);
    //                 this.clampPoints.push(clampMid);
    //                 let pointEnd = this._geoCurves[i].getPointAt(1);
    //                 let clampEnd = new CableMiddleClamp(editor, this, pointEnd, `${i + 1}`);
    //                 this.clampPoints.push(clampEnd);
    //             }
    //         } else {
    //             let clampMid = new CableMiddleClamp(editor, this, pointMid, `center-${i}-${i + 1}`);
    //             this.clampPoints.push(clampMid);
    //         }
    //     }
    //     return this.clampPoints;
    // }


    copy(pline: PlineT): this;
    copy(cable: CableT): this;
    copy(pline: any): this {
        super.copy(pline); // 调用父类的 copy 方法
        if (pline instanceof CableT) {
            this.group = pline?.group;
        }
        return this;
    }

    clone(): this {
        let cable = new CableT().copy(this) as this; // 创建新实例并复制属性
        cable.uuid = ModelUtils.generateUUID(); // 生成新的 UUID
        return cable;
    }

    toJson(rename: boolean = false) {
        const json = super.toJson();
        return {
            ...json,
            group: this.group,
            extraData: this.extraData,
        };
    }

    fromJson(json) {
        super.fromJson(json);
        this.group = json?.group ? json?.group : {}; //适配早期的图元没有该字段
        this.extraData = json?.extraData ? json?.extraData : {};
        return this;
    }


}